import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { cn } from './utils/shadcn-utils';
const Input = React.forwardRef(({ className, type, onAutocompleteSearch, onAutocompleteSelect, autocompleteDebounceMs = 300, autocompleteFillAuto = true, ...props }, ref) => {
    const [autocompleteItems, setAutocompleteItems] = React.useState([]);
    const [showAutocomplete, setShowAutocomplete] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(false);
    const inputRef = React.useRef(null);
    const dropdownRef = React.useRef(null);
    const debounceTimeoutRef = React.useRef(undefined);
    // Combine refs
    React.useImperativeHandle(ref, () => inputRef.current, []);
    const handleInputChange = React.useCallback((e) => {
        const value = e.target.value;
        // Call original onChange if provided
        if (props.onChange) {
            props.onChange(e);
        }
        // Handle autocomplete
        if (onAutocompleteSearch) {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
            setIsLoading(true);
            setShowAutocomplete(true);
            debounceTimeoutRef.current = setTimeout(async () => {
                try {
                    const results = await onAutocompleteSearch(value);
                    setAutocompleteItems(results);
                }
                catch (error) {
                    console.error('Autocomplete search error:', error);
                    setAutocompleteItems([]);
                }
                finally {
                    setIsLoading(false);
                }
            }, autocompleteDebounceMs);
        }
    }, [onAutocompleteSearch, autocompleteDebounceMs, props.onChange]);
    const handleAutocompleteSelect = React.useCallback((item) => {
        if (autocompleteFillAuto && inputRef.current) {
            inputRef.current.value = item.label;
            // Trigger onChange event if provided
            if (props.onChange) {
                const syntheticEvent = {
                    target: inputRef.current,
                    currentTarget: inputRef.current
                };
                props.onChange(syntheticEvent);
            }
        }
        if (onAutocompleteSelect) {
            onAutocompleteSelect(item);
        }
        setShowAutocomplete(false);
        setAutocompleteItems([]);
    }, [onAutocompleteSelect, autocompleteFillAuto, props.onChange]);
    const handleInputFocus = React.useCallback((e) => {
        if (props.onFocus) {
            props.onFocus(e);
        }
        // Trigger search on focus regardless of input value
        if (onAutocompleteSearch) {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
            setIsLoading(true);
            debounceTimeoutRef.current = setTimeout(async () => {
                try {
                    const results = await onAutocompleteSearch(e.target.value);
                    setAutocompleteItems(results);
                    if (results.length > 0) {
                        setShowAutocomplete(true);
                    }
                }
                catch (error) {
                    console.error('Autocomplete search error:', error);
                    setAutocompleteItems([]);
                }
                finally {
                    setIsLoading(false);
                }
            }, autocompleteDebounceMs);
        }
        else if (autocompleteItems.length > 0) {
            setShowAutocomplete(true);
        }
    }, [props.onFocus, onAutocompleteSearch, autocompleteItems.length, autocompleteDebounceMs]);
    // Handle clicking outside to close dropdown
    React.useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target) && inputRef.current && !inputRef.current.contains(event.target)) {
                setShowAutocomplete(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);
    // Cleanup timeout on unmount
    React.useEffect(() => {
        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        };
    }, []);
    return (_jsxs("div", { className: "relative w-full", children: [_jsx("input", { type: type, autoFocus: props.autoFocus, className: cn('flex h-9 w-full bg-input rounded-md border border-input-border px-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-[var(--focus-ring-width)] focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm', onAutocompleteSearch && isLoading && 'pr-9', // Add right padding when loading
                className), ref: inputRef, ...props, onChange: handleInputChange, onFocus: handleInputFocus }), onAutocompleteSearch && isLoading && (_jsx("div", { className: "absolute right-3 top-1/2 -translate-y-1/2", children: _jsx("div", { className: "h-4 w-4 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" }) })), onAutocompleteSearch && showAutocomplete && autocompleteItems.length > 0 && (_jsx("div", { ref: dropdownRef, className: "absolute top-full left-0 right-0 z-50 mt-1 max-h-60 overflow-auto rounded-md border border-input-border bg-popover shadow-md", children: autocompleteItems.map(item => (_jsx("div", { className: "cursor-pointer px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground", onClick: () => handleAutocompleteSelect(item), children: item.label }, item.id))) }))] }));
});
Input.displayName = 'Input';
export { Input };
//# sourceMappingURL=input.js.map